"use client"

import React, { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Button } from "@/Components/ui2/button"
import { Input } from "@/Components/ui2/input"
import { Label } from "@/Components/ui2/label"
import { Phone, Mail, MapPin, Linkedin, Youtube, Instagram, ArrowLeft, X } from "lucide-react"
import Navbar from "@/Components/navbar"
import { useAuth } from "../../context/AuthContext.jsx"
import phonepeService from "../../services/phonepeService.js"

// API base — uploads are served by the backend server, not the frontend
const API_BASE = import.meta.env.VITE_API_URL || ""

// Helper function to construct proper image URL
const getImageUrl = (imagePath) => {
  if (!imagePath || imagePath === 'null' || imagePath === 'undefined') {
    return "/images/circuit-board.png";
  }
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  if (!imagePath.includes('/')) {
    return `${API_BASE}/uploads/${imagePath}`;
  }
  if (imagePath.startsWith('uploads/')) {
    return `${API_BASE}/${imagePath}`;
  }
  if (imagePath.startsWith('/uploads/')) {
    return `${API_BASE}${imagePath}`;
  }
  return imagePath;
};



// No static data; fetch from backend

export default function WorkshopDetailPage() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false)
  const [isProcessingPayment, setIsProcessingPayment] = useState(false)
  const [loading, setLoading] = useState(true)
  const [workshop, setWorkshop] = useState(null)
  
  // Coupon state
  const [couponCode, setCouponCode] = useState("")
  const [couponError, setCouponError] = useState("")
  const [couponSuccess, setCouponSuccess] = useState("")
  const [finalAmount, setFinalAmount] = useState(null)
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false)

  const [formData, setFormData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    mobile: user?.phone || "",
  })

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('auth_token')
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        const res = await fetch(`${API_BASE}/api/workshop/${slug}`, { credentials: 'include', headers })
        if (!res.ok) throw new Error('Failed to load workshop')
        const data = await res.json()
        setWorkshop(data.workshop)
      } catch (e) {
        console.error('Failed to fetch workshop:', e)
      } finally {
        setLoading(false)
      }
    }
    if (slug) load()
  }, [slug])

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="container mx-auto px-4 py-8 text-center">
          <h1 className="text-2xl font-bold" style={{ color: "#FFFFF0" }}>Loading...</h1>
        </div>
      </div>
    )
  }

  if (!workshop) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="container mx-auto px-4 py-8 text-center">
          <h1 className="text-3xl font-bold mb-4" style={{ color: "#FFFFF0" }}>
            Workshop not found
          </h1>
          <Button
            onClick={() => navigate("/workshop")}
            style={{ backgroundColor: "#B88AFF", color: "#FFFFF0" }}
          >
            Back to Workshops
          </Button>
        </div>
      </div>
    )
  }

  const handleEnrollClick = () => {
    if (!user) {
      alert("Please login to enroll in this workshop!")
      navigate("/auth")
      return
    }
    setShowEnrollmentModal(true)
  }

  const handleFormSubmit = async (e) => {
    e.preventDefault()
    setIsProcessingPayment(true)
    
    try {
      // Generate unique order ID
      const orderId = phonepeService.generateOrderId()
      
      // Extract amount
      const baseAmount = parseInt(workshop.price.replace(/[^0-9]/g, ''))
      const amount = finalAmount !== null ? finalAmount : baseAmount
      
      // Prepare user data
      const userData = {
        userId: user._id,
        name: formData.name,
        email: formData.email,
        mobile: formData.mobile
      }
      
      // Prepare workshop data
      const workshopData = {
        id: workshop.id,
        title: workshop.title,
        price: workshop.price
      }
      
      // Initialize PhonePe payment
      const paymentResponse = await phonepeService.initiatePayment(orderId, amount, userData, workshopData)
      
      if (paymentResponse.success) {
        // Save enrollment data with pending status
        const enrollmentData = {
          workshopId: workshop.id,
          workshopTitle: workshop.title,
          userId: user._id,
          userName: formData.name,
          userEmail: formData.email,
          userMobile: formData.mobile,
          amount: amount,
          orderId: orderId,
          transactionId: paymentResponse.transactionId,
          paymentStatus: "pending",
          enrollmentDate: new Date().toISOString(),
          status: "payment_pending"
        }
        
        console.log("Workshop payment initiated:", enrollmentData)
        
        // Redirect to PhonePe payment page
        window.location.href = paymentResponse.paymentUrl
      } else {
        throw new Error('Payment initiation failed')
      }
    } catch (error) {
      console.error("PhonePe payment error:", error)
      alert("An error occurred while initiating payment. Please try again.")
      setIsProcessingPayment(false)
    }
  }

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return
    setIsApplyingCoupon(true)
    setCouponError("")
    setCouponSuccess("")
    
    try {
      const baseAmount = parseInt(workshop.price.replace(/[^0-9]/g, ''))
      const token = localStorage.getItem('auth_token')
      const res = await fetch(`${API_BASE}/api/coupon/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        credentials: 'include',
        body: JSON.stringify({
          code: couponCode,
          amount: baseAmount,
          type: 'workshop',
          itemId: workshop._id || workshop.id
        })
      })
      
      const data = await res.json()
      if (res.ok && data.valid) {
        setFinalAmount(data.finalAmount)
        setCouponSuccess(data.message)
      } else {
        setCouponError(data.message || 'Invalid coupon code')
        setFinalAmount(null)
      }
    } catch (err) {
      setCouponError('Error applying coupon. Please try again.')
      setFinalAmount(null)
    } finally {
      setIsApplyingCoupon(false)
    }
  }

  const handleRemoveCoupon = () => {
    setCouponCode("")
    setCouponError("")
    setCouponSuccess("")
    setFinalAmount(null)
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <Navbar />

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <Button
          onClick={() => navigate("/workshop")}
          variant="ghost"
          className="mb-6 hover:opacity-80"
          style={{ color: "var(--text-primary)", backgroundColor: "var(--bg-card)", border: "1px solid var(--accent-primary)" }}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Workshops
        </Button>

        {/* Workshop Hero Section */}
        <div className="text-center mb-12 py-16 rounded-3xl shadow-2xl" style={{ backgroundColor: "var(--bg-secondary)" }}>
          <h1 className="text-4xl md:text-5xl font-bold mb-8" style={{ color: "var(--text-primary)" }}>
            {workshop.title}
          </h1>
          <div className="max-w-4xl mx-auto mb-8">
            <img
              src={getImageUrl(workshop.image)}
              alt={workshop.title}
              className="w-full h-64 md:h-80 object-cover rounded-2xl shadow-xl"
            />
          </div>
          <div className="flex flex-wrap justify-center items-center gap-8 text-lg">
            <span style={{ color: "var(--accent-primary)" }}>{workshop.createdBy || workshop.instructor}</span>
            <span style={{ color: "var(--accent-primary)" }}>{`Duration: ${workshop.duration} hours`}</span>
            <span className="text-3xl font-bold" style={{ color: "var(--text-primary)" }}>
              {`₹${workshop.price}`}
            </span>
          </div>
        </div>

        {/* About This Workshop */}
        <section className="mb-12 p-8 rounded-2xl shadow-xl" style={{ backgroundColor: "var(--bg-card)", border: "2px solid var(--accent-primary)", backdropFilter: "blur(10px)" }}>
          <h2 className="text-3xl font-bold mb-6 pb-4 border-b-4" style={{ color: "var(--text-primary)", borderColor: "var(--accent-primary)" }}>
            About This Workshop
          </h2>
          <p className="text-lg leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            {workshop.about}
          </p>
        </section>

        {/* Syllabus */}
        <section className="mb-12 p-8 rounded-2xl shadow-xl" style={{ backgroundColor: "var(--bg-card)", border: "2px solid var(--accent-primary)", backdropFilter: "blur(10px)" }}>
          <h2 className="text-3xl font-bold mb-6 pb-4 border-b-4" style={{ color: "var(--text-primary)", borderColor: "var(--accent-primary)" }}>
            Workshop Syllabus
          </h2>
          <div className="space-y-4">
            {(workshop.syllabus || []).map((item, index) => (
              <div key={index} className="p-4 rounded-lg" style={{ backgroundColor: "var(--border-color)" }}>
                <p className="text-lg" style={{ color: "var(--text-secondary)" }}>
                  {item}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Who Should Attend */}
        <section className="mb-12 p-8 rounded-2xl shadow-xl" style={{ backgroundColor: "var(--bg-card)", border: "2px solid var(--accent-primary)", backdropFilter: "blur(10px)" }}>
          <h2 className="text-3xl font-bold mb-6 pb-4 border-b-4" style={{ color: "var(--text-primary)", borderColor: "var(--accent-primary)" }}>
            Who Should Attend
          </h2>
          <div className="space-y-4">
            {(workshop.whoShouldAttend || []).map((item, index) => (
              <div key={index} className="p-4 rounded-lg" style={{ backgroundColor: "var(--border-color)" }}>
                <p className="text-lg" style={{ color: "var(--text-secondary)" }}>
                  {item}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Prerequisites */}
        <section className="mb-12 p-8 rounded-2xl shadow-xl" style={{ backgroundColor: "var(--bg-card)", border: "2px solid var(--accent-primary)", backdropFilter: "blur(10px)" }}>
          <h2 className="text-3xl font-bold mb-6 pb-4 border-b-4" style={{ color: "var(--text-primary)", borderColor: "var(--accent-primary)" }}>
            Prerequisites
          </h2>
          <div className="space-y-4">
            {(workshop.prerequisites || []).map((item, index) => (
              <div key={index} className="p-4 rounded-lg" style={{ backgroundColor: "var(--border-color)" }}>
                <p className="text-lg" style={{ color: "var(--text-secondary)" }}>
                  {item}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Enrollment Button */}
        <div className="text-center">
          <Button
            onClick={handleEnrollClick}
            className="px-8 py-4 text-xl"
            style={{ background: "var(--accent-gradient)", color: "white" }}
          >
            {user ? `Enroll in Workshop - ₹${workshop.price}` : "Login to Enroll"}
          </Button>
        </div>
      </main>

      {/* Enrollment Modal */}
      {showEnrollmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--bg-secondary)] text-[var(--text-primary)] rounded-2xl p-8 max-w-md w-full border-2 border-[var(--accent-primary)]">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold" style={{ color: "var(--text-primary)" }}>
                Enroll in Workshop
              </h2>
              <button
                onClick={() => setShowEnrollmentModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleFormSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name" className="text-lg" style={{ color: "var(--text-secondary)" }}>
                  Full Name
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="mt-2"
                  style={{ backgroundColor: "var(--bg-card)", border: "1px solid var(--accent-primary)", color: "var(--text-primary)" }}
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-lg" style={{ color: "var(--text-secondary)" }}>
                  Email
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="mt-2"
                  style={{ backgroundColor: "var(--bg-card)", border: "1px solid var(--accent-primary)", color: "var(--text-primary)" }}
                />
              </div>
              <div>
                <Label htmlFor="mobile" className="text-lg" style={{ color: "var(--text-secondary)" }}>
                  Mobile Number
                </Label>
                <Input
                  id="mobile"
                  name="mobile"
                  type="tel"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  required
                  className="mt-2"
                  style={{ backgroundColor: "var(--bg-card)", border: "1px solid var(--accent-primary)", color: "var(--text-primary)" }}
                />
              </div>
              
              <div className="bg-[var(--border-color)] p-4 rounded-lg">
                <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
                  <strong>Workshop:</strong> {workshop.title}
                </p>
                <div className="flex justify-between items-center mt-2">
                  <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
                    <strong>Price:</strong>
                  </p>
                  <p className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>
                    <span className={finalAmount !== null ? "line-through text-gray-500 text-xs mr-2" : ""}>
                      ₹{parseInt(workshop.price.replace(/[^0-9]/g, ''))}
                    </span>
                    {finalAmount !== null && <span style={{ color: "var(--accent-primary)" }}>₹{finalAmount}</span>}
                  </p>
                </div>
              </div>
              
              {/* Coupon Section */}
              <div className="bg-[var(--bg-card)] p-4 rounded-lg border border-[var(--border-color)]">
                <Label className="text-sm mb-2 block" style={{ color: "var(--text-secondary)" }}>Have a coupon code?</Label>
                <div className="flex gap-2">
                  <Input
                    value={couponCode}
                    onChange={e => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="Enter code"
                    disabled={couponSuccess || isApplyingCoupon}
                    className="flex-1"
                    style={{ backgroundColor: "var(--bg-primary)", border: "1px solid var(--border-color)", color: "var(--text-primary)", textTransform: "uppercase" }}
                  />
                  {couponSuccess ? (
                    <Button type="button" onClick={handleRemoveCoupon} variant="outline" className="px-3" style={{ color: "#f87171", borderColor: "#f87171" }}>
                      Remove
                    </Button>
                  ) : (
                    <Button type="button" onClick={handleApplyCoupon} disabled={!couponCode || isApplyingCoupon} style={{ background: "var(--bg-secondary)", color: "var(--accent-primary)", border: "1px solid var(--accent-primary)" }}>
                      {isApplyingCoupon ? "..." : "Apply"}
                    </Button>
                  )}
                </div>
                {couponError && <p className="text-xs mt-2" style={{ color: "#f87171" }}>{couponError}</p>}
                {couponSuccess && <p className="text-xs mt-2 font-medium" style={{ color: "#34d399" }}>{couponSuccess}</p>}
              </div>

              <div className="flex gap-4">
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={isProcessingPayment}
                  style={{ background: "var(--accent-gradient)", color: "white" }}
                >
                  {isProcessingPayment ? "Processing Payment..." : `Pay ₹${finalAmount !== null ? finalAmount : parseInt(workshop.price.replace(/[^0-9]/g, ''))}`}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEnrollmentModal(false)}
                  disabled={isProcessingPayment}
                  style={{ borderColor: "var(--accent-primary)", color: "var(--accent-primary)" }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

